import React from 'react';
import { alpha, Box, Divider, Skeleton, Stack, TableCell, TableRow } from '@mui/material';
import { PREP_COL, prepTdNumSx, prepTdSx } from '../constants';

const prepSk = {
  bgcolor: '#e8edf3',
  transform: 'none',
  '&::after': { animationDuration: '1.1s' },
};

export function PrepSk({ variant = 'text', width, height = 12, sx = {} }) {
  return (
    <Skeleton
      variant={variant}
      width={width}
      height={height}
      animation="wave"
      sx={{ ...prepSk, borderRadius: variant === 'circular' ? '50%' : 0.5, ...sx }}
    />
  );
}

export function PrepCandidatesStatsSkeleton() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', minWidth: 0, width: '100%' }}>
      <PrepSk width={118} height={18} sx={{ borderRadius: 0.75 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <PrepSk variant="rectangular" width={120} height={5} sx={{ borderRadius: 99 }} />
        <PrepSk width={200} height={12} />
      </Box>
      <PrepSk width={168} height={12} />
      <PrepSk width={72} height={20} sx={{ borderRadius: 99 }} />
    </Box>
  );
}

export function PrepCandidatesRowSkeleton({ index }) {
  const signalLines = index % 3 === 0 ? 3 : 2;
  const reasonLines = index % 2 === 0 ? 2 : 1;
  return (
    <TableRow sx={{ '&:nth-of-type(odd)': { bgcolor: '#fafbfc' } }}>
      <TableCell sx={{ ...prepTdSx, width: PREP_COL.ticker }}>
        <PrepSk width={index % 2 === 0 ? 48 : 56} height={14} sx={{ bgcolor: alpha('#2563eb', 0.12) }} />
        <PrepSk width={index % 3 === 0 ? '96%' : '88%'} height={10} sx={{ mt: 0.35 }} />
        <PrepSk width="72%" height={10} sx={{ mt: 0.28 }} />
      </TableCell>
      <TableCell align="right" sx={{ ...prepTdNumSx, width: PREP_COL.score }}>
        <PrepSk width={36} height={16} sx={{ ml: 'auto' }} />
      </TableCell>
      <TableCell sx={{ ...prepTdSx, width: PREP_COL.signal }}>
        <PrepSk width={index % 2 === 0 ? '88%' : '76%'} height={14} sx={{ bgcolor: alpha('#16a34a', 0.1) }} />
        {Array.from({ length: signalLines }).map((_, i) => (
          <PrepSk key={i} width={i === 0 ? '94%' : '82%'} height={10} sx={{ mt: 0.3 }} />
        ))}
      </TableCell>
      <TableCell sx={{ ...prepTdSx, width: PREP_COL.sector }}>
        <PrepSk width="90%" height={12} />
        <PrepSk width="62%" height={10} sx={{ mt: 0.3 }} />
      </TableCell>
      <TableCell align="right" sx={prepTdNumSx}>
        <PrepSk width={52} height={14} sx={{ ml: 'auto' }} />
      </TableCell>
      <TableCell align="right" sx={prepTdNumSx}>
        <PrepSk width={44} height={14} sx={{ ml: 'auto' }} />
      </TableCell>
      <TableCell align="right" sx={prepTdNumSx}>
        <PrepSk width={38} height={12} sx={{ ml: 'auto' }} />
      </TableCell>
      <TableCell align="right" sx={prepTdNumSx}>
        <PrepSk width={32} height={12} sx={{ ml: 'auto' }} />
      </TableCell>
      <TableCell sx={prepTdSx}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
          <PrepSk variant="circular" width={6} height={6} />
          <PrepSk width={index % 2 === 0 ? 72 : 56} height={12} />
        </Box>
      </TableCell>
      <TableCell sx={{ ...prepTdSx, width: PREP_COL.reasons }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
          {Array.from({ length: reasonLines }).map((_, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.6 }}>
              <PrepSk variant="circular" width={5} height={5} sx={{ mt: 0.45, flexShrink: 0 }} />
              <PrepSk width={i === 0 ? '92%' : '78%'} height={i === 0 ? 11 : 10} />
            </Box>
          ))}
        </Box>
      </TableCell>
    </TableRow>
  );
}

export function PrepHistoryRowSkeleton({ index }) {
  const pickCount = 4 + (index % 3);
  return (
    <TableRow sx={{ '&:nth-of-type(odd)': { bgcolor: '#fafbfc' } }}>
      <TableCell sx={{ ...prepTdSx, width: 108 }}>
        <PrepSk width={56} height={13} />
      </TableCell>
      <TableCell align="right" sx={prepTdSx}>
        <PrepSk width={32} height={14} sx={{ ml: 'auto', bgcolor: alpha('#16a34a', 0.1) }} />
      </TableCell>
      <TableCell align="right" sx={prepTdSx}>
        <PrepSk width={22} height={13} sx={{ ml: 'auto' }} />
      </TableCell>
      <TableCell align="right" sx={prepTdSx}>
        <PrepSk width={18} height={13} sx={{ ml: 'auto', bgcolor: alpha('#16a34a', 0.08) }} />
      </TableCell>
      <TableCell align="right" sx={prepTdSx}>
        <PrepSk width={28} height={13} sx={{ ml: 'auto' }} />
      </TableCell>
      <TableCell align="right" sx={prepTdSx}>
        <PrepSk width={40} height={13} sx={{ ml: 'auto' }} />
        <PrepSk width={48} height={9} sx={{ mt: 0.35, ml: 'auto' }} />
      </TableCell>
      <TableCell align="right" sx={prepTdSx}>
        <PrepSk width={40} height={13} sx={{ ml: 'auto' }} />
        <PrepSk width={52} height={9} sx={{ mt: 0.35, ml: 'auto' }} />
      </TableCell>
      <TableCell sx={{ ...prepTdSx, py: 0.65 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
          {Array.from({ length: pickCount }).map((_, i) => (
            <PrepSk key={i} width={38 + (i % 3) * 8} height={22} sx={{ borderRadius: 0.75, flexShrink: 0 }} />
          ))}
        </Box>
      </TableCell>
    </TableRow>
  );
}

export function PrepDrawerDetailSkeleton() {
  return (
    <Stack spacing={1.5}>
      <Box sx={{ p: 1.25, border: `1px solid ${alpha('#16a34a', 0.35)}`, borderRadius: 1.25, bgcolor: alpha('#16a34a', 0.06) }}>
        <PrepSk width={88} height={10} sx={{ mb: 0.6 }} />
        <PrepSk width="100%" height={12} />
        <PrepSk width="78%" height={12} sx={{ mt: 0.35 }} />
      </Box>

      <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1.25, bgcolor: '#fff' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <PrepSk width={72} height={12} />
          <PrepSk width={56} height={12} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Box key={i}>
              <PrepSk width={48} height={9} sx={{ mb: 0.35 }} />
              <PrepSk width={i % 2 === 0 ? '70%' : '55%'} height={16} />
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1.25, bgcolor: '#fff' }}>
        <PrepSk width={100} height={12} sx={{ mb: 1 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Box key={i}>
              <PrepSk width={64} height={9} sx={{ mb: 0.35 }} />
              <PrepSk width="58%" height={14} />
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1.25, bgcolor: '#fff' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.1 }}>
          <PrepSk width={80} height={12} />
          <PrepSk width={64} height={12} />
        </Box>
        {[['#0ea5e9', 86], ['#dc2626', 86], ['#16a34a', 86]].map(([barColor, labelW]) => (
          <Box key={barColor} sx={{ display: 'flex', alignItems: 'baseline', gap: 1.1, mb: 0.85 }}>
            <Box sx={{ width: 3, alignSelf: 'stretch', borderRadius: 999, bgcolor: barColor, minHeight: 20 }} />
            <PrepSk width={labelW} height={10} />
            <PrepSk width={88} height={18} />
            <PrepSk width={36} height={12} sx={{ ml: 'auto' }} />
          </Box>
        ))}
        <Divider sx={{ my: 1.1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <PrepSk width={48} height={14} />
          <PrepSk width={72} height={14} />
        </Box>
      </Box>

      <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1.25, bgcolor: '#fff' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
          <PrepSk width={120} height={12} />
          <PrepSk width={72} height={10} />
        </Box>
        <PrepSk variant="rectangular" width="100%" height={150} sx={{ borderRadius: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 0.6, flexWrap: 'wrap' }}>
          {[48, 56, 44, 40].map((w) => (
            <PrepSk key={w} width={w} height={10} />
          ))}
        </Box>
      </Box>

      <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1.25, bgcolor: '#fff' }}>
        <PrepSk width={96} height={12} sx={{ mb: 1 }} />
        <Stack spacing={1}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Box key={i}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                <PrepSk width={72} height={12} />
                <Box sx={{ display: 'flex', gap: 0.6 }}>
                  <PrepSk width={56} height={16} sx={{ borderRadius: 0.6 }} />
                  <PrepSk width={22} height={12} />
                </Box>
              </Box>
              <PrepSk variant="rectangular" width="100%" height={4} sx={{ borderRadius: 99 }} />
              <PrepSk width="88%" height={10} sx={{ mt: 0.4 }} />
            </Box>
          ))}
        </Stack>
      </Box>

      <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1.25, bgcolor: '#fff' }}>
        <PrepSk width={128} height={12} sx={{ mb: 1 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.25 }}>
          {[0, 1, 2, 3].map((i) => (
            <Box key={i}>
              <PrepSk width={80} height={9} sx={{ mb: 0.35 }} />
              <PrepSk width="65%" height={16} />
              <PrepSk width="50%" height={10} sx={{ mt: 0.25 }} />
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1.25, bgcolor: '#fff' }}>
        <PrepSk width={104} height={12} sx={{ mb: 1 }} />
        {[0, 1, 2].map((i) => (
          <Box key={i} sx={{ display: 'flex', gap: 0.7, mb: 0.8 }}>
            <PrepSk variant="circular" width={6} height={6} sx={{ mt: 0.45 }} />
            <PrepSk width={`${90 - i * 8}%`} height={12} />
          </Box>
        ))}
      </Box>
    </Stack>
  );
}
